import React, { useState, useEffect } from 'react';

interface YouTubeCredentialsProps {
  userId: string;
  isOpen: boolean;
  onClose: () => void;
  onCredentialsUpdated: () => void;
}

interface CredentialStatus {
  hasCredentials: boolean;
  hasApiKey: boolean;
  hasOAuth: boolean;
  createdAt?: string;
}

const YouTubeCredentials: React.FC<YouTubeCredentialsProps> = ({ 
  userId, 
  isOpen, 
  onClose, 
  onCredentialsUpdated 
}) => {
  const [credentialType, setCredentialType] = useState<'api' | 'oauth'>('api');
  const [apiKey, setApiKey] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [refreshToken, setRefreshToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [credentialStatus, setCredentialStatus] = useState<CredentialStatus | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchCredentialStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userId]);

  const fetchCredentialStatus = async () => {
    try {
      const response = await fetch(`http://localhost:3001/api/credentials/${userId}`);
      const data = await response.json();
      setCredentialStatus(data);
    } catch (error) {
      console.error('Error fetching credential status:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const payload: any = { userId };

      if (credentialType === 'api') {
        if (!apiKey.trim()) {
          setMessage('API Key is required');
          setLoading(false);
          return;
        }
        payload.apiKey = apiKey.trim();
      } else {
        if (!clientId.trim() || !clientSecret.trim() || !refreshToken.trim()) {
          setMessage('All OAuth fields are required');
          setLoading(false);
          return;
        }
        payload.clientId = clientId.trim();
        payload.clientSecret = clientSecret.trim();
        payload.refreshToken = refreshToken.trim();
      }

      const response = await fetch('http://localhost:3001/api/credentials', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('✅ Credentials saved and validated successfully!');
        setTimeout(() => {
          onCredentialsUpdated();
          onClose();
        }, 1500);
      } else {
        setMessage(`❌ Error: ${data.error}`);
      }
    } catch (error) {
      setMessage(`❌ Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete your YouTube credentials?')) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`http://localhost:3001/api/credentials/${userId}`, {
        method: 'DELETE',
      });

      const data = await response.json();
      setMessage(data.success ? '✅ Credentials deleted successfully' : '❌ Failed to delete credentials');
      
      if (data.success) {
        setTimeout(() => {
          fetchCredentialStatus();
          onCredentialsUpdated();
        }, 1000);
      }
    } catch (error) {
      setMessage(`❌ Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '80vh',
        overflowY: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, color: '#1a1a1a' }}>YouTube API Credentials</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666'
            }}
          >
            ×
          </button>
        </div>

        {credentialStatus?.hasCredentials && (
          <div style={{
            backgroundColor: '#f0f9ff',
            border: '1px solid #bfdbfe',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '20px'
          }}>
            <p style={{ margin: 0, color: '#1e40af', fontSize: '14px' }}>
              ✅ You have {credentialStatus.hasApiKey ? 'API Key' : 'OAuth'} credentials configured
              {credentialStatus.createdAt && (
                <span style={{ color: '#6b7280' }}>
                  {' '}• Added {new Date(credentialStatus.createdAt).toLocaleDateString()}
                </span>
              )}
            </p>
          </div>
        )}

        <div style={{ marginBottom: '20px' }}>
          <p style={{ color: '#374151', marginBottom: '16px' }}>
            Add your YouTube API credentials to access transcripts from private videos and channels you manage.
          </p>
          
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
              <input
                type="radio"
                name="credentialType"
                value="api"
                checked={credentialType === 'api'}
                onChange={(e) => setCredentialType(e.target.value as 'api')}
                style={{ marginRight: '8px' }}
              />
              <strong>API Key</strong> (Easier setup, read-only access)
            </label>
            
            <label style={{ display: 'flex', alignItems: 'center' }}>
              <input
                type="radio"
                name="credentialType"
                value="oauth"
                checked={credentialType === 'oauth'}
                onChange={(e) => setCredentialType(e.target.value as 'oauth')}
                style={{ marginRight: '8px' }}
              />
              <strong>OAuth</strong> (Full access, more complex setup)
            </label>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {credentialType === 'api' ? (
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontWeight: '500', marginBottom: '8px' }}>
                YouTube Data API v3 Key
              </label>
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="AIza..."
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
              <p style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0 0 0' }}>
                Get this from Google Cloud Console → APIs & Services → Credentials
              </p>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontWeight: '500', marginBottom: '8px' }}>
                  Client ID
                </label>
                <input
                  type="text"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  placeholder="123456789-abc..."
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontWeight: '500', marginBottom: '8px' }}>
                  Client Secret
                </label>
                <input
                  type="password"
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                  placeholder="GOCSPX-..."
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontWeight: '500', marginBottom: '8px' }}>
                  Refresh Token
                </label>
                <input
                  type="password"
                  value={refreshToken}
                  onChange={(e) => setRefreshToken(e.target.value)}
                  placeholder="1//..."
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
                <p style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0 0 0' }}>
                  Generate using OAuth 2.0 Playground or your app's authorization flow
                </p>
              </div>
            </>
          )}

          {message && (
            <div style={{
              padding: '12px',
              borderRadius: '6px',
              marginBottom: '16px',
              backgroundColor: message.includes('✅') ? '#f0fdf4' : '#fef2f2',
              border: `1px solid ${message.includes('✅') ? '#bbf7d0' : '#fecaca'}`,
              color: message.includes('✅') ? '#166534' : '#dc2626',
              fontSize: '14px'
            }}>
              {message}
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            {credentialStatus?.hasCredentials && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={loading}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1
                }}
              >
                Delete
              </button>
            )}
            
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                padding: '10px 20px',
                backgroundColor: '#f3f4f6',
                color: '#374151',
                border: 'none',
                borderRadius: '6px',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              Cancel
            </button>
            
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '10px 20px',
                backgroundColor: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1
              }}
            >
              {loading ? 'Validating...' : 'Save & Validate'}
            </button>
          </div>
        </form>

        <div style={{
          marginTop: '24px',
          padding: '16px',
          backgroundColor: '#f9fafb',
          borderRadius: '8px',
          fontSize: '12px',
          color: '#6b7280'
        }}>
          <p style={{ margin: '0 0 8px 0', fontWeight: '500' }}>Setup Instructions:</p>
          <ol style={{ margin: 0, paddingLeft: '16px' }}>
            <li>Go to <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer">Google Cloud Console</a></li>
            <li>Enable YouTube Data API v3</li>
            <li>Create credentials (API Key or OAuth 2.0)</li>
            <li>Copy and paste your credentials above</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default YouTubeCredentials;